import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSubscriptionManagerV1Service, serviceName } from '../service';
import { IsMultiEligibleForSelfUpgradeOrTrialIO, ServiceQueries } from '../types';
import { useSubscriptionManagerV1Queries } from './use-subscription-manager-v1-queries';
import {
  UseSubscriptionManagerV1QueryEndpointArgs,
  useSubscriptionManagerV1Query,
} from './use-subscription-manager-v1-query';

/**
 * A hook that returns a query for the `IsMultiEligibleForSelfUpgradeOrTrial` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useIsMultiEligibleForSelfUpgradeOrTrialQuery = <
  E = unknown,
  D = IsMultiEligibleForSelfUpgradeOrTrialIO['output']
>(
  args: UseSubscriptionManagerV1QueryEndpointArgs<'IsMultiEligibleForSelfUpgradeOrTrial', E, D>
) =>
  useSubscriptionManagerV1Query({
    endpointName: 'IsMultiEligibleForSelfUpgradeOrTrial',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `IsMultiEligibleForSelfUpgradeOrTrial` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useIsMultiEligibleForSelfUpgradeOrTrialQueries = <
  E = unknown,
  D = IsMultiEligibleForSelfUpgradeOrTrialIO['output']
>(
  argsArr: UseSubscriptionManagerV1QueryEndpointArgs<'IsMultiEligibleForSelfUpgradeOrTrial', E, D>[]
) =>
  useSubscriptionManagerV1Queries(
    argsArr.map((args) => ({
      endpointName: 'IsMultiEligibleForSelfUpgradeOrTrial',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `IsMultiEligibleForSelfUpgradeOrTrial` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchIsMultiEligibleForSelfUpgradeOrTrial = () => {
  const queryClient = useQueryClient();

  const fetchIsMultiEligibleForSelfUpgradeOrTrial = async <
    E = unknown,
    D = IsMultiEligibleForSelfUpgradeOrTrialIO['output']
  >({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSubscriptionManagerV1QueryEndpointArgs<'IsMultiEligibleForSelfUpgradeOrTrial', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      IsMultiEligibleForSelfUpgradeOrTrialIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'IsMultiEligibleForSelfUpgradeOrTrial'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        IsMultiEligibleForSelfUpgradeOrTrialIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'IsMultiEligibleForSelfUpgradeOrTrial'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'IsMultiEligibleForSelfUpgradeOrTrial'>({
          serviceName,
          endpointName: 'IsMultiEligibleForSelfUpgradeOrTrial',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSubscriptionManagerV1Service.IsMultiEligibleForSelfUpgradeOrTrial(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchIsMultiEligibleForSelfUpgradeOrTrial;
};
